/*
 * Session page "Download CSV" for the Care panels (Change requests, Change history): pure column
 * maps over what the panels render. Screenshot links are presigned and expire, so only Yes/No.
 */
import { csv_file_name, ist_datetime_text } from '../../../util_components/csv_export/to_csv';
import { KIND_LABELS } from '../../../util_components/care_request/care_request_rules';
import { match_view, who_asked_view } from '../../../util_components/care_request/who_asked_rules';
import { change_line, note_label, older_record_label } from './change_history_rules';

export const session_csv_name = (session_uuid, table) =>
  csv_file_name({ page: `session-${String(session_uuid || '').slice(-8)}`, table });

const screenshot_text = (r) => {
  if (r.screenshot_url) return 'Yes';
  return r.has_screenshot || r.screenshot_link_error ? 'Link unavailable' : 'No';
};

// One side of a change: an instant (reschedule) as IST, else the teacher name (teacher change).
const side = (time, teacher) => (time ? ist_datetime_text(time) : teacher || '');

/* Change requests: one row per change, joined to its Care record (null when details are missing). */
export const change_request_rows = (view) => {
  const records = {};
  ((view && view.records) || []).forEach((r) => {
    records[r.uuid] = r;
  });
  return ((view && view.changes) || []).map((change) => ({
    change,
    record: change.record_uuid ? records[change.record_uuid] || null : null
  }));
};

const rec = (row, key) => (row.record ? row.record[key] : '');
const who_of = (row) =>
  row.record ? who_asked_view(row.record.who_asked, row.record.initiated_by) : null;

export const CHANGE_REQUEST_CSV_COLUMNS = [
  { header: 'Change', value: (r) => KIND_LABELS[r.change.change_kind] || r.change.change_kind },
  { header: 'From', value: (r) => side(r.change.from_time, r.change.from_teacher) },
  { header: 'To', value: (r) => side(r.change.to_time, r.change.to_teacher) },
  { header: 'Change made at (IST)', value: (r) => r.change.change_made_at, kind: 'datetime' },
  {
    header: 'Request initiated by',
    value: (r) => (who_of(r) ? who_of(r).shown : 'Details missing')
  },
  { header: 'Who asked status', value: (r) => (who_of(r) ? who_of(r).marker_text : '') },
  { header: 'Who asked detail', value: (r) => (who_of(r) ? who_of(r).detail : '') },
  { header: 'Requested at (IST)', value: (r) => rec(r, 'requested_at'), kind: 'datetime' },
  { header: 'Note', value: (r) => rec(r, 'note') },
  { header: 'Note for teacher', value: (r) => rec(r, 'teacher_note') },
  { header: 'Entered by', value: (r) => rec(r, 'created_by_name') },
  { header: 'Entered at (IST)', value: (r) => rec(r, 'created_date'), kind: 'datetime' },
  { header: 'Edited by', value: (r) => (rec(r, 'modified_by') ? rec(r, 'modified_by_name') : '') },
  {
    header: 'Edited at (IST)',
    value: (r) => (rec(r, 'modified_by') ? rec(r, 'modified_date') : ''),
    kind: 'datetime'
  },
  { header: 'Screenshot', value: (r) => (r.record ? screenshot_text(r.record) : '') },
  { header: 'Care record UUID', value: (r) => rec(r, 'uuid') || '' }
];

/* Change history: every entry (not only the 10 on screen before "Show all"). */
const req = (e) => e.request || null;
const req_field = (e, key) => (req(e) ? req(e)[key] : '');

export const CHANGE_HISTORY_CSV_COLUMNS = [
  { header: 'When (IST)', key: 'at', kind: 'datetime' },
  { header: 'Changed by', value: (e) => e.actor_name || 'Unknown user' },
  { header: 'Change', key: 'title' },
  { header: 'Details', value: (e) => (e.changes || []).map(change_line) },
  { header: 'Note type', value: (e) => (e.note ? note_label(e) : '') },
  { header: 'Note', key: 'note' },
  { header: 'Older record', value: (e) => older_record_label(e) },
  {
    header: 'Request initiated by',
    value: (e) => (req(e) ? who_asked_view(req(e).who_asked, req(e).initiated_by).shown : '')
  },
  {
    header: 'Who asked status',
    value: (e) => (req(e) ? who_asked_view(req(e).who_asked, req(e).initiated_by).marker_text : '')
  },
  { header: 'Move match', value: (e) => (req(e) ? match_view(req(e).match).marker_text : '') },
  { header: 'Requested at (IST)', value: (e) => req_field(e, 'requested_at'), kind: 'datetime' },
  { header: 'Internal note', value: (e) => req_field(e, 'note') },
  { header: 'Note for teacher', value: (e) => req_field(e, 'teacher_note') },
  { header: 'Screenshot', value: (e) => (req(e) ? screenshot_text(req(e)) : '') },
  { header: 'Recorded by', value: (e) => req_field(e, 'recorded_by_name') },
  { header: 'Entry ID', key: 'id' }
];
